import { useState, useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Canvas } from '@react-three/fiber';
import { Stars, OrbitControls, Html } from '@react-three/drei';
import type { Book } from '../lib/api/types';

interface AtlasScene3DProps {
  books: Book[];
  onSelect: (slug: string) => void;
}

// 分类 → 星球色调映射
const CATEGORY_COLOR: Record<string, string> = {
  勇气: '#4ade80',
  冒险: '#fbbf24',
  友谊: '#60a5fa',
  童话: '#a78bfa',
  科学: '#22d3ee',
  历史: '#f472b6',
  自然: '#34d399',
};
const DEFAULT_COLOR = '#8ab4ff';

function categoryColor(category?: string): string {
  if (!category) return DEFAULT_COLOR;
  return CATEGORY_COLOR[category] ?? DEFAULT_COLOR;
}

// 页数 → 星球半径映射（0.3–0.65）
function pageRadius(pagesCount: number): number {
  return Math.min(0.65, Math.max(0.3, 0.3 + pagesCount * 0.035));
}

function BookNode({
  book,
  index,
  total,
  onSelect,
}: {
  book: Book;
  index: number;
  total: number;
  onSelect: (slug: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<any>(null);
  const groupRef = useRef<any>(null);
  const ringRef = useRef<any>(null);

  const angle = useMemo(() => (index / Math.max(1, total)) * Math.PI * 2, [index, total]);
  const radius = 5.4;
  const basePosition: [number, number, number] = [
    Math.cos(angle) * radius,
    Math.sin(angle) * radius * 0.62,
    ((index % 3) - 1) * 0.7,
  ];
  const color = categoryColor(book.category);
  const baseR = pageRadius(book.pages_count || 5);
  const hoverScale = hovered ? 1.28 : 1;

  // 悬浮动画：每颗星球独立相位，y 轴微幅波动 + 缓慢自转
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (meshRef.current) {
      meshRef.current.position.y = basePosition[1] + Math.sin(t * 0.6 + index * 1.7) * 0.18;
    }
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.12 + index * 0.8;
    }
    if (ringRef.current) {
      ringRef.current.rotation.z = t * 0.35 + index * 2.1;
    }
  });

  return (
    <group ref={groupRef} position={basePosition}>
      {/* 星球主体 */}
      <mesh
        ref={meshRef}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(book.slug);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = 'auto';
        }}
        scale={hoverScale}
      >
        <sphereGeometry args={[baseR, 28, 28]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={hovered ? 2.2 : 0.75}
          roughness={0.25}
          metalness={0.12}
        />
      </mesh>

      {/* 星球表面斑点（用更小的球体模拟地貌） */}
      <mesh position={[baseR * 0.35, baseR * 0.2, baseR * 0.4]} scale={0.28}>
        <sphereGeometry args={[baseR, 12, 12]} />
        <meshStandardMaterial color="#ffffff" opacity={0.18} transparent />
      </mesh>
      <mesh position={[-baseR * 0.25, -baseR * 0.3, baseR * 0.3]} scale={0.22}>
        <sphereGeometry args={[baseR, 10, 10]} />
        <meshStandardMaterial color="#000000" opacity={0.15} transparent />
      </mesh>

      {/* 免费书：多层光环 */}
      {book.is_free && (
        <>
          <mesh ref={ringRef} rotation={[Math.PI / 2.4, 0, 0]}>
            <ringGeometry args={[baseR * 1.35, baseR * 1.55, 48]} />
            <meshBasicMaterial color={color} transparent opacity={hovered ? 0.55 : 0.25} side={2} />
          </mesh>
          <mesh rotation={[Math.PI / 2.4 + 0.25, 0, 0]}>
            <ringGeometry args={[baseR * 1.65, baseR * 1.78, 48]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={hovered ? 0.25 : 0.1} side={2} />
          </mesh>
        </>
      )}

      {/* 付费书：条纹纹理环 */}
      {!book.is_free && (
        <mesh rotation={[Math.PI / 2.4, 0, 0]}>
          <torusGeometry args={[baseR * 1.45, 0.055, 12, 48]} />
          <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.6} transparent opacity={hovered ? 0.8 : 0.4} />
        </mesh>
      )}

      {/* Hover Tooltip */}
      {hovered && (
        <Html center position={[0, baseR + 1.15, 0]} zIndexRange={[20, 0]} style={{ pointerEvents: 'none' }}>
          <div
            style={{
              background: 'rgba(10,12,28,0.92)',
              border: `1.5px solid ${color}`,
              color: '#fff',
              padding: '12px 16px',
              borderRadius: '14px',
              fontSize: '13px',
              maxWidth: '240px',
              boxShadow: `0 0 28px ${color}55`,
              fontFamily: 'system-ui, sans-serif',
              lineHeight: 1.6,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '6px', color }}>{book.title}</div>
            <div style={{ opacity: 0.8, marginBottom: '6px' }}>
              {book.category || '童话'} · {book.pages_count || '?'} 页 · {book.is_free ? '免费' : `¥${(book.price_cents / 100).toFixed(0)}`}
            </div>
            <div style={{ opacity: 0.55, fontSize: '12px' }}>
              {book.description?.slice(0, 60) || 'AI 生成的互动绘本'}
              {(book.description?.length ?? 0) > 60 ? '…' : ''}
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}

/**
 * Atlas 3D 星图增强（Phase 2 Week 8 · Ticket 02）：
 * - 分类着色（勇气=绿、冒险=橙、友谊=蓝、童话=紫、科学=青）
 * - 页数映射半径（0.3–0.65）
 * - 免费书 = 多层光环；付费书 = 条纹纹理环
 * - 悬浮动画：useFrame Math.sin 每帧更新，无 gsap 内存泄漏
 * - Hover tooltip：书名 + 元数据 + 简介 60 字
 */
export function AtlasScene3D({ books, onSelect }: AtlasScene3DProps) {
  return (
    <Canvas
      camera={{ position: [0, 0, 11], fov: 50 }}
      gl={{ antialias: true, alpha: true }}
      style={{ width: '100%', height: '100%' }}
    >
      <ambientLight intensity={0.35} />
      <pointLight position={[0, 0, 6]} intensity={30} color="#8ab4ff" />
      <Stars radius={60} depth={40} count={2200} factor={3.2} saturation={0} fade speed={0.6} />
      <OrbitControls enablePan={false} enableZoom={true} minDistance={6} maxDistance={20} autoRotate autoRotateSpeed={0.4} />
      {books.map((book, i) => (
        <BookNode key={book.id} book={book} index={i} total={books.length} onSelect={onSelect} />
      ))}
    </Canvas>
  );
}
